import "react-bootstrap-typeahead/css/Typeahead.css";
import "./index.css";

import { resolveMeasurementPose } from "@corvus/corvus-map/model";
import { limit, orderBy, where } from "firebase/firestore";
import mergeImages from "merge-images";
import moment from "moment-timezone";
import queryString from "query-string";
import React, { useMemo } from "react";
import {
    Button,
    ButtonGroup,
    Card,
    Dropdown,
    OverlayTrigger,
    Spinner,
    Tooltip,
} from "react-bootstrap";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCode,
    FaDownload,
    FaMap,
} from "react-icons/fa";
import ModalImage from "react-modal-image";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { toMeasurementQueryDownloadItem } from "../../api/api.jsx";
import {
    MeasurementCollection,
    MeasurementCollectionGroup,
    MeasurementDocument,
    OverlayCollection,
    useMeasurementDocument,
    useMissionDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import DevMenuButton from "../../components/DevMenuButton";
import { DownloadButton } from "../../components/DownloadButton/index.jsx";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import { MeasurementTagger } from "../../components/ItemTagger";
import Layout, { Title } from "../../components/Layout/Layout";
import MeasurementDeletedAlert from "../../components/MeasurementDeletedAlert/index.jsx";
import OverlaySelector from "../../components/OverlaySelector";
import StorageDevMenuItem from "../../components/StorageDevMenuItem/index.jsx";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { getFirebaseStorageDownloadUrl } from "../../corvusFirebase.js";
import urls from "../../urls.jsx";

export default function ImagePage() {
    const { organizationId, locationId, missionId, measurementId } =
        useParams();
    const location = useLocation();
    const [mission] = useMissionDocument(organizationId, locationId, missionId);
    const [measurement, isLoading] = useMeasurementDocument(
        organizationId,
        locationId,
        measurementId,
    );
    const isNotDeleted = measurement && !measurement?.deletedAt;
    const [nextMeasurement, setNextMeasurement] = React.useState(undefined);
    const [previousMeasurement, setPreviousMeasurement] =
        React.useState(undefined);
    const [view, setView] = React.useState("data");
    const [imageUrl, setImageUrl] = React.useState(null);
    const [overlays, setOverlays] = React.useState([]);
    const [backgroundUrl, setBackgroundUrl] = React.useState(null);
    const [selectedOverlays, setSelectedOverlays] = React.useState(() => {
        const overlaysQuery = queryString.parse(location.search).overlays;
        return overlaysQuery
            ? Array.isArray(overlaysQuery)
                ? overlaysQuery
                : [overlaysQuery]
            : [];
    });
    const [availableOverlays, setAvailableOverlays] = React.useState(
        mission?.overlays || [],
    );
    const [isOverlayCompositionLoading, setIsOverlayCompositionLoading] =
        React.useState(false);
    const compositionRequestId = React.useRef(0);
    const resolvedMeasurementPose = measurement
        ? resolveMeasurementPose(measurement, "telemetry-optimized")
        : null;
    const mapUrl =
        measurement &&
        measurement?.meta?.mission?.id !== undefined &&
        resolvedMeasurementPose
            ? `${urls.locationMap(organizationId, locationId)}?missionId=${measurement.meta.mission.id}&x=${resolvedMeasurementPose.pose.position.x}&y=${resolvedMeasurementPose.pose.position.y}`
            : null;

    const { getUserIsAdmin, userClaims } = useUser();
    const isAdmin = useMemo(() => getUserIsAdmin(), [getUserIsAdmin]);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (
            !isLoading ||
            organizationId === undefined ||
            locationId === undefined
        ) {
            if (!measurement) {
                MeasurementCollectionGroup.list([
                    where("id", "==", measurementId),
                    ...(getUserIsAdmin()
                        ? []
                        : // This part of the query is needed so firebase security rules can ensure the document can be provided to the user
                          [
                              where(
                                  "organizationId",
                                  "==",
                                  userClaims?.organizationId,
                              ),
                          ]),
                ]).then((m) => {
                    const foundMeasurement = m?.[0];
                    if (foundMeasurement !== undefined) {
                        navigate(
                            urls.measurement(
                                foundMeasurement.organizationId,
                                foundMeasurement.locationId,
                                foundMeasurement.meta.mission.id,
                                foundMeasurement.id,
                            ),
                            { replace: true },
                        );
                    }
                });
                return;
            } else {
                if (missionId !== measurement.meta.mission.id) {
                    navigate(
                        urls.measurement(
                            measurement.organizationId,
                            measurement.locationId,
                            measurement.meta.missionId,
                            measurement.id,
                        ),
                        { replace: true },
                    );
                }
            }
        }
    }, [
        organizationId,
        locationId,
        navigate,
        measurement,
        missionId,
        isLoading,
        measurementId,
        getUserIsAdmin,
        userClaims,
    ]);

    const resetPage = React.useCallback(() => {
        compositionRequestId.current += 1;
        setBackgroundUrl(null);
        setOverlays([]);
        setImageUrl(null);
        setIsOverlayCompositionLoading(false);
    }, []);

    const hasNextMeasurement = React.useCallback(() => {
        return nextMeasurement !== undefined;
    }, [nextMeasurement]);

    const hasPreviousMeasurement = React.useCallback(() => {
        return previousMeasurement !== undefined;
    }, [previousMeasurement]);

    const visitNextMeasurement = React.useCallback(() => {
        if (hasNextMeasurement()) {
            resetPage();
            navigate(
                `${urls.measurement(organizationId, locationId, missionId, nextMeasurement.id)}${location.search}`,
            );
        }
    }, [
        hasNextMeasurement,
        navigate,
        organizationId,
        locationId,
        missionId,
        nextMeasurement,
        location.search,
        resetPage,
    ]);

    const visitPreviousMeasurement = React.useCallback(() => {
        if (hasPreviousMeasurement()) {
            resetPage();
            navigate(
                `${urls.measurement(organizationId, locationId, missionId, previousMeasurement.id)}${location.search}`,
            );
        }
    }, [
        hasPreviousMeasurement,
        navigate,
        organizationId,
        locationId,
        missionId,
        previousMeasurement,
        location.search,
        resetPage,
    ]);

    React.useEffect(() => {
        if (!measurement) return;

        (async () => {
            const [prev] = await MeasurementCollection(
                organizationId,
                locationId,
            ).list([
                where(
                    "event.summary.MEASUREMENT_STARTED",
                    "<",
                    measurement.event.summary.MEASUREMENT_STARTED,
                ),
                where("meta.mission.id", "==", missionId),
                orderBy("event.summary.MEASUREMENT_STARTED", "desc"),
                limit(1),
            ]);

            const [next] = await MeasurementCollection(
                organizationId,
                locationId,
            ).list([
                where(
                    "event.summary.MEASUREMENT_STARTED",
                    ">",
                    measurement.event.summary.MEASUREMENT_STARTED,
                ),
                where("meta.mission.id", "==", missionId),
                orderBy("event.summary.MEASUREMENT_STARTED", "asc"),
                limit(1),
            ]);

            setPreviousMeasurement(prev);
            setNextMeasurement(next);
        })();
    }, [organizationId, locationId, missionId, measurement]);

    React.useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "ArrowLeft") {
                visitPreviousMeasurement();
            } else if (event.key === "ArrowRight") {
                visitNextMeasurement();
            }
        };

        // Add event listeners when the component mounts
        window.addEventListener("keydown", handleKeyDown);

        // Remove event listeners when the component unmounts
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [visitPreviousMeasurement, visitNextMeasurement]);

    // get all the overlays that are available for this measurement
    React.useEffect(() => {
        let unsub;
        let cancelled = false;
        if (organizationId && locationId && measurementId && isNotDeleted) {
            // subscribe to the overlay images
            async function getOverlayUrls(overlays) {
                const urlOverlays = await Promise.all(
                    overlays
                        .filter(
                            (overlay) =>
                                isAdmin ||
                                !(overlay.background
                                    ? overlay.background
                                    : false),
                        )
                        .map(async (overlay) => {
                            try {
                                const bucketPath = `organizations/${organizationId}/locations/${locationId}/measurements/${measurementId}/overlays/${overlay.id}.png`;
                                return await getFirebaseStorageDownloadUrl(
                                    bucketPath,
                                ).then((url) => {
                                    return { ...overlay, url: url };
                                });
                            } catch {
                                return null;
                            }
                        }),
                );
                const availableUrlOverlays = urlOverlays.filter(
                    (overlay) => overlay !== null,
                );
                if (cancelled) return;

                setOverlays(availableUrlOverlays);
                const remainingOverlayNames = availableUrlOverlays.map(
                    (overlay) => overlay.name,
                );
                const removedOverlays = overlays
                    .filter(
                        (overlay) =>
                            !remainingOverlayNames.includes(overlay.name),
                    )
                    .map((overlay) => overlay.name);

                setAvailableOverlays(
                    (mission?.overlays || []).filter(
                        (overlay) => !removedOverlays.includes(overlay),
                    ),
                );
            }
            unsub = OverlayCollection(
                organizationId,
                locationId,
                measurementId,
            ).subscribeList(getOverlayUrls);
        }
        return () => {
            cancelled = true;
            unsub?.();
        };
    }, [
        organizationId,
        locationId,
        measurementId,
        isAdmin,
        mission?.overlays,
        isNotDeleted,
    ]);

    React.useEffect(() => {
        const requestId = ++compositionRequestId.current;
        const selectedOverlayUrls = selectedOverlays
            .map(
                (overlayName) =>
                    overlays.find((overlay) => overlay.name === overlayName)
                        ?.url,
            )
            .filter((url) => url !== undefined);

        if (!backgroundUrl) return;

        if (selectedOverlayUrls.length === 0) {
            setIsOverlayCompositionLoading(false);
            setImageUrl(backgroundUrl);
            return;
        }

        let cancelled = false;
        setIsOverlayCompositionLoading(true);

        mergeImages([backgroundUrl, ...selectedOverlayUrls], {
            crossOrigin: "Anonymous",
            format: "image/jpeg",
            quality: 0.9,
        })
            .then((output) => {
                if (!cancelled && requestId === compositionRequestId.current) {
                    setImageUrl(output);
                }
            })
            .catch((error) => {
                console.log(selectedOverlays, error);
            })
            .finally(() => {
                if (!cancelled && requestId === compositionRequestId.current) {
                    setIsOverlayCompositionLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [backgroundUrl, overlays, selectedOverlays]);

    // See which ones are selected
    const handleOverlaySelectionChange = React.useCallback(
        (newSelectedOverlays) => {
            const query = queryString.parse(location.search);
            delete query.overlays;
            if (newSelectedOverlays.length > 0) {
                query.overlays = newSelectedOverlays;
            }

            setSelectedOverlays(newSelectedOverlays);
            const search = queryString.stringify(query);
            navigate({ search: search ? `?${search}` : "" }, { replace: true });
        },
        [location.search, navigate],
    );

    React.useEffect(() => {
        const overlaysQuery = queryString.parse(location.search).overlays;
        const selectedFromQuery = overlaysQuery
            ? Array.isArray(overlaysQuery)
                ? overlaysQuery
                : [overlaysQuery]
            : [];
        setSelectedOverlays((currentSelected) =>
            currentSelected.length === selectedFromQuery.length &&
            currentSelected.every(
                (overlay, index) => overlay === selectedFromQuery[index],
            )
                ? currentSelected
                : selectedFromQuery,
        );
    }, [location.search]);

    // set the measurement image to either just the measurement, or combine it with the overlays and display that
    React.useEffect(() => {
        let cancelled = false;
        if (organizationId && locationId && measurementId && isNotDeleted) {
            const bucketPath = `organizations/${organizationId}/locations/${locationId}/measurements/${measurementId}.jpg`;
            getFirebaseStorageDownloadUrl(bucketPath).then((url) => {
                if (cancelled) return;
                setBackgroundUrl(url);
                setImageUrl(url);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [organizationId, locationId, measurementId, isNotDeleted]);

    React.useEffect(() => {
        if (
            missionId === "undefined" &&
            measurement?.meta?.mission?.id !== undefined
        ) {
            navigate(
                urls.measurement(
                    organizationId,
                    locationId,
                    measurement?.meta?.mission?.id,
                    measurementId,
                ),
                { replace: true },
            );
        }
    });
    const userTimezone = moment.tz.guess();
    const format = "YYYY-MM-DD HH:mm:ss";
    const timestamp = new Date(
        measurement?.event?.summary?.MEASUREMENT_STARTED,
    ).getTime();
    const dateMoment = moment(timestamp).tz(userTimezone);

    return (
        <Layout>
            <title>{`Measurement ${dateMoment.format(format)} | MyCorvus`}</title>
            <Breadcrumbs
                path={breadcrumbPaths.image}
                mission={mission ?? null}
            />
            <Card className="mb-4">
                <Title
                    backButtonUrl={urls.missionMeasurements(
                        organizationId,
                        locationId,
                        missionId,
                    )}
                    title={
                        <>
                            Measurement{" "}
                            {measurement && (
                                <TimeDisplay
                                    timestamp={
                                        new Date(
                                            measurement.event.summary
                                                .MEASUREMENT_STARTED,
                                        ).getTime() / 1000
                                    }
                                    format="YYYY-MM-DD HH:mm:ss"
                                    organizationId={organizationId}
                                    locationId={locationId}
                                />
                            )}
                        </>
                    }
                >
                    {getUserIsAdmin() && (
                        <DevMenuButton>
                            <Dropdown.Item
                                onClick={() =>
                                    setView(view === "data" ? "meta" : "data")
                                }
                            >
                                <FaCode
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                {view === "data" && <>View Meta</>}
                                {view === "meta" && <>View Data</>}
                            </Dropdown.Item>
                            {organizationId && locationId && measurementId && (
                                <>
                                    <FirestoreDevMenuItem
                                        as={Dropdown.Item}
                                        path={
                                            MeasurementDocument(
                                                organizationId,
                                                locationId,
                                                measurementId,
                                            ).path
                                        }
                                    />
                                    <StorageDevMenuItem
                                        as={Dropdown.Item}
                                        path={`organizations/${organizationId}/locations/${locationId}/measurements/${measurementId}.jpg`}
                                        isFile={true}
                                    />
                                    {isNotDeleted && (
                                        <DownloadButton
                                            as={Dropdown.Item}
                                            downloadItems={[
                                                toMeasurementQueryDownloadItem({
                                                    organizationId,
                                                    locationId,
                                                    measurementId,
                                                }),
                                            ]}
                                            checkAvailability={false}
                                            filename={`measurement_${measurementId}.zip`}
                                            buttonText={"Download"}
                                            variant="outline-secondary"
                                            icon={
                                                <FaDownload className="me-2" />
                                            }
                                        />
                                    )}
                                </>
                            )}
                            {measurement && (
                                <>
                                    {mapUrl && (
                                        <Dropdown.Item as={Link} to={mapUrl}>
                                            <FaMap
                                                className="me-2"
                                                style={{ marginTop: "-2px" }}
                                            />
                                            View on map
                                        </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                        as={Link}
                                        to={urls.flight(
                                            organizationId,
                                            locationId,
                                            measurement.meta.flight.id,
                                        )}
                                    >
                                        Go to Flight
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        as={Link}
                                        to={urls.locationMission(
                                            organizationId,
                                            locationId,
                                            measurement.meta.mission.id,
                                        )}
                                    >
                                        Go to Mission
                                    </Dropdown.Item>
                                </>
                            )}
                        </DevMenuButton>
                    )}
                    <ButtonGroup>
                        <Button
                            variant="outline-secondary"
                            className="px-3"
                            onClick={visitPreviousMeasurement}
                            disabled={!hasPreviousMeasurement()}
                        >
                            <FaArrowLeft
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            className="px-3"
                            onClick={visitNextMeasurement}
                            disabled={!hasNextMeasurement()}
                        >
                            <FaArrowRight
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                        </Button>
                        {!getUserIsAdmin() && mapUrl && (
                            <OverlayTrigger
                                delay={{ hide: 250, show: 250 }}
                                overlay={<Tooltip>View on map</Tooltip>}
                                placement="top"
                            >
                                <Button
                                    variant="outline-secondary"
                                    className="px-3"
                                    onClick={() => {
                                        navigate(mapUrl);
                                    }}
                                >
                                    <FaMap style={{ marginTop: "-2px" }} />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </ButtonGroup>
                </Title>
                {view === "meta" && (
                    <Card.Body className="p-4 pt-0">
                        <CodeBlock name="Measurement" code={measurement} />
                    </Card.Body>
                )}
                {view === "data" && (
                    <>
                        {getUserIsAdmin() && (
                            <Card.Body className="p-4 pt-0">
                                <MeasurementTagger
                                    selected={measurement?.tags || []}
                                    onChange={(newTags) =>
                                        MeasurementDocument(
                                            organizationId,
                                            locationId,
                                            measurementId,
                                        ).update({ tags: newTags })
                                    }
                                />
                            </Card.Body>
                        )}
                        <Card.Body className="p-4 pt-0">
                            {measurement?.deletedAt ? (
                                <MeasurementDeletedAlert
                                    deletedTimestamp={measurement?.deletedAt}
                                    organizationId={organizationId}
                                    locationId={locationId}
                                />
                            ) : (
                                <div className="container">
                                    {availableOverlays.length > 0 && (
                                        <div className="mb-2">
                                            <div className="fw-bold small mb-1 d-flex align-items-center">
                                                <span>Overlay(s)</span>

                                                {isOverlayCompositionLoading && (
                                                    <Spinner
                                                        className="ms-2"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Loading overlay...
                                                        </span>
                                                    </Spinner>
                                                )}
                                            </div>

                                            <OverlaySelector
                                                overlays={availableOverlays}
                                                selectedOverlays={
                                                    selectedOverlays
                                                }
                                                onChange={
                                                    handleOverlaySelectionChange
                                                }
                                            />
                                        </div>
                                    )}
                                    <ModalImage
                                        // hideZoom={true}
                                        hideDownload={true}
                                        small={imageUrl}
                                        large={imageUrl}
                                        className="backgroundImage"
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </>
                )}
            </Card>
        </Layout>
    );
}
